import { useCallback, useEffect } from "react"
import { useParams, useNavigate, useLocation } from "react-router-dom"
import { useTheme, Box, useMediaQuery } from "@mui/material"
import _ from "lodash"

import { useNormalizedRouterSearchParams } from "hooks"
import { useMap } from "hooks/map/useMap"
import { useOrdinalMap } from "hooks/ordinal"
import {
  useFlyToMap,
  useGetUserLocationView,
  useGetMapViewFromUrl,
} from "hooks/map"
import { useTranslation } from "providers/i18n"
import { useAppUI } from "providers/ui"
import { useVenue } from "providers/venue"
import { useGeoLocation } from "providers/geolocation"
import { pluralize } from "utils/string"

import { OrdinalSelector } from "components/OrdinalSelector"
import { VenueSelector } from "components/VenueSelector"
import { SearchToggleButton } from "components/domains/search/SearchToggleButton"
import { OrdinalSelectMenu } from "components/OrdinalSelector/OrdinalSelectMenu"
import { OrdinalSelectSplide } from "components/OrdinalSelector/OrdinalSelectSplide"
import { FeatureChipToggleBar } from "components/domains/features/ChipToggleBar"
import { MyLocationButton } from "components/Button"
import { convertMapViewObjectToPath } from "utils/map"

import { PageLayout } from "layouts"
import { ScrollableTab } from "components/ScrollableTab"

export const MapOrdinal = ({ Layout = PageLayout }) => {
  const { isLoading, userLocation, locationVenue } = useGeoLocation()
  const navigate = useNavigate()
  const { ordinal } = useParams()
  const { search: searchQueryString } = useLocation()
  const { userLocation: userLocationParams } = useNormalizedRouterSearchParams<{
    userLocation: string
  }>()
  const { t } = useTranslation()
  const { venues, isMultiOrdinalVenue, isMultiVenueProject } = useVenue()
  const {
    viewingVenueId,
    viewingVenueView,
    viewingVenueLevels,
    quickSearchCategories,
    onChangeVenue,
    locationVenueId,
    locationOrdinal,
  } = useMap()
  const { openSearch, isKioskMode } = useAppUI()

  const parsedOrdinal = Number(ordinal)
  const hasUserLocationAtCurrentFloor = locationOrdinal === parsedOrdinal

  useOrdinalMap(parsedOrdinal)
  const userLocationView = useGetUserLocationView()
  const { mapView } = useGetMapViewFromUrl()
  useFlyToMap(mapView || viewingVenueView)

  const theme = useTheme()
  const matchDownMd = useMediaQuery(theme.breakpoints.down("md"))

  const { styleOverrides = {} } = theme.components[`AppMap`] || {}

  const {
    pageLayout,
    searchBarContainer,
    toolbarContainer,
    quickLinkContainer, //TODO:: planning about quick search's working flow later
    locationButtonContainer,
    venueSelector,
    ordinalSelectMenu,
    ordinalSelectSplide,
    ordinalSelector,
  } = styleOverrides

  const onClickOrdinal = useCallback(
    (ordinal) => {
      navigate(
        `/maps/${
          ordinal !== null ? `ordinal/${ordinal}${searchQueryString}` : ""
        }`,
        { replace: true }
      )
    },
    [navigate, searchQueryString]
  )

  const handleClickQuickSearch = useCallback(
    (featureType, amenityCategory) => {
      //This condition prevent navigation when ordinal or viewingVenueId is undefined
      if (_.isNil(ordinal) || _.isNil(viewingVenueId)) return
      const pluralType = pluralize(featureType)
      navigate(
        `/maps/${pluralType}/${amenityCategory}?from-ordinal=${ordinal}&venue=${viewingVenueId}`
      )
    },
    [navigate, ordinal, viewingVenueId]
  )

  const handleClickMyLocation = useCallback(() => {
    //This condition prevent navigation when ordinal is undefined
    if (_.isNil(ordinal) || !userLocation) return
    const userOrdinal = _.get(userLocation, "properties.ordinal", 0)
    const mapViewPath = convertMapViewObjectToPath(userLocationView)
    navigate(`/maps/ordinal/${userOrdinal}?mv=${mapViewPath}`, {
      replace: true,
    })
  }, [navigate, ordinal, userLocationView, userLocation])

  useEffect(() => {
    if (
      !isLoading &&
      userLocationParams &&
      userLocation &&
      !_.isNil(locationVenue?.id)
    ) {
      const {
        properties: { ordinal: userOrdinal },
      } = userLocation
      navigate(`/maps/ordinal/${userOrdinal}?venue=${locationVenue?.id}`, {
        replace: true,
      })
    } else if (
      !isLoading &&
      userLocationParams &&
      !_.isNil(locationVenue?.id)
    ) {
      navigate(`/maps/ordinal/${ordinal}?venue=${locationVenue?.id}`, {
        replace: true,
      })
    }
  }, [
    navigate,
    ordinal,
    isLoading,
    userLocation,
    locationVenue,
    userLocationParams,
  ])

  useEffect(() => {
    if (hasUserLocationAtCurrentFloor) handleClickMyLocation()
  }, [hasUserLocationAtCurrentFloor, handleClickMyLocation])

  const ordinalOptionsHeight = matchDownMd ? "136px" : "202px"

  return (
    <>
      <Layout sx={pageLayout}>
        <Box sx={toolbarContainer}>
          {!isKioskMode && (
            <Box sx={searchBarContainer}>
              <SearchToggleButton
                placeholder={t("Search for your destination")}
                onClick={openSearch}
              />
            </Box>
          )}
          <Box sx={quickLinkContainer}>
            <ScrollableTab>
              <FeatureChipToggleBar
                categories={quickSearchCategories}
                onClick={handleClickQuickSearch}
              />
            </ScrollableTab>
          </Box>
        </Box>
        {isMultiOrdinalVenue && (
          <Box sx={ordinalSelector}>
            <OrdinalSelector
              selectedOrdinal={parsedOrdinal}
              levels={viewingVenueLevels}
              onClickOrdinal={onClickOrdinal}
            />
          </Box>
        )}
        {isMultiVenueProject && (
          <Box sx={venueSelector}>
            <VenueSelector
              selectedVenue={viewingVenueId}
              venues={venues}
              onClickVenue={onChangeVenue}
              currentVenue={locationVenueId}
            />
          </Box>
        )}
        {isMultiOrdinalVenue && (
          <>
            <Box sx={ordinalSelectMenu}>
              <OrdinalSelectMenu
                selectedOrdinal={parsedOrdinal}
                levels={viewingVenueLevels}
                onChangeOrdinal={onClickOrdinal}
              />
            </Box>
            <Box sx={ordinalSelectSplide}>
              <OrdinalSelectSplide
                selectedOrdinal={parsedOrdinal}
                levels={viewingVenueLevels}
                onChangeOrdinal={onClickOrdinal}
                options={{
                  perPage: 4,
                  focus: 2,
                  height: ordinalOptionsHeight,
                }}
              />
            </Box>
          </>
        )}
        {userLocation && (
          <Box sx={locationButtonContainer}>
            <MyLocationButton onClick={handleClickMyLocation} />
          </Box>
        )}
      </Layout>
    </>
  )
}
